import { useState, useEffect, useRef, useMemo } from 'react';
import { X, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { createOrganization, slugify } from '@/db/organizations';
import { useOrganization } from '@/context/OrganizationContext';
import { generateA11yId } from '@/utils/accessibility';
import { handleError } from '@/utils/errorHandler';

interface CreateOrgModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateOrgModal({ isOpen, onClose }: CreateOrgModalProps) {
  const { refreshOrgs, setCurrentOrg } = useOrganization();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = useMemo(() => generateA11yId('create-org-modal-title'), []);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setSlug('');
      setSlugManuallyEdited(false);
      setIsCreating(false);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleNameChange = (value: string) => {
    setName(value);
    setError(null);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim() || isCreating) return;

    setIsCreating(true);
    setError(null);
    try {
      const org = await createOrganization(name.trim(), slug.trim());
      await refreshOrgs();
      // Auto-select the new org
      setCurrentOrg({ id: org.id, name: org.name, slug: org.slug, avatar_url: null, created_at: '', created_by: '' });
      onClose();
    } catch (err) {
      handleError(err, { level: 'error', message: 'Errore nella creazione dell\'organizzazione' });
      setError(err instanceof Error ? err.message : 'Errore durante la creazione');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in origin-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
          <h2 id={titleId} className="text-lg font-semibold text-text-primary">Crea organizzazione</h2>
          <button onClick={onClose} aria-label="Chiudi" className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="org-name" className="block text-sm font-medium text-text-secondary mb-2">Nome</label>
              <input
                ref={inputRef}
                id="org-name"
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="La mia azienda"
                className={clsx(
                  'w-full px-4 py-2.5 rounded-lg border transition-colors',
                  error ? 'border-accent-rose' : 'border-border-primary',
                  'bg-surface-primary text-text-primary placeholder-text-quaternary',
                  'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
                )}
                disabled={isCreating}
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="org-slug" className="block text-sm font-medium text-text-secondary mb-2">Slug (URL)</label>
              <input
                id="org-slug"
                type="text"
                value={slug}
                onChange={(e) => { setSlug(slugify(e.target.value)); setSlugManuallyEdited(true); setError(null); }}
                placeholder="la-mia-azienda"
                className="w-full px-4 py-2.5 rounded-lg border border-border-primary bg-surface-primary text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent transition-colors"
                disabled={isCreating}
              />
              <p className="mt-1 text-xs text-text-quaternary">Identificativo univoco per l'organizzazione</p>
            </div>
            {error && (
              <div className="flex items-start gap-2 text-accent-rose">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} disabled={isCreating} className="px-4 py-2 rounded-lg text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors disabled:opacity-50">
              Annulla
            </button>
            <button type="submit" disabled={!name.trim() || !slug.trim() || isCreating} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-accent-violet hover:bg-accent-violet/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {isCreating ? 'Creazione...' : 'Crea organizzazione'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
